import React, { useContext, useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Form } from 'semantic-ui-react';
import { get } from 'lodash';
import { latLngToUTM_NS, latLngToUTMWithBand } from 'src/utils/useful_functions';
import { parseCoords } from 'src/pages/map/SiteModePanel';
import { FormResetContext } from 'src/pages/pmp/utils';
import messages from 'src/messages';

interface UTMCoordinateInputProps {
  site: any;
  setModified: (modified: boolean) => void;
  disabled: boolean;
  latitudeField?: string; // Optional: defaults to 'latitude'
  longitudeField?: string; // Optional: defaults to 'longitude'
  utmReferenceField?: string; // Optional: defaults to 'utm_reference'
}

export const UTMCoordinateInput: React.FC<UTMCoordinateInputProps> = ({
  site,
  setModified,
  disabled,
  latitudeField = 'latitude',
  longitudeField = 'longitude',
  utmReferenceField = 'utm_reference',
}) => {
  const {
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const resetValue = useContext(FormResetContext);
  const { formatMessage } = useIntl();
  const latLngFormat = useSelector(
    (state: any) => state.mainFrame.prefs.latLngFormat
  );

  const isUTM_NS = latLngFormat === formatMessage(messages.utmNorthSouthHemisphere);
  const isUTM_Band = latLngFormat === formatMessage(messages.utmWithLatitudeBand);

  // Watch latitude and longitude from form
  const formLat = useWatch({ control, name: latitudeField });
  const formLng = useWatch({ control, name: longitudeField });
  const utmReference = useWatch({ control, name: utmReferenceField });

  // Use form values if available, otherwise fall back to site values
  const currentLat = formLat ?? site.latitude;
  const currentLng = formLng ?? site.longitude;

  // Calculate reference value for display (initial/default value)
  const utmReferenceValue = isUTM_NS
    ? latLngToUTM_NS(currentLat, currentLng)
    : isUTM_Band
    ? latLngToUTMWithBand(currentLat, currentLng)
    : 'N/A';

  // Track if user is actively editing to prevent overwriting their input
  const [isUserEditing, setIsUserEditing] = useState(false);
  const previousResetValue = useRef(resetValue);

  // Update reference field when form is reset (resetValue changes)
  // This ensures reference field is recalculated from site lat/lon on reset
  // Only update when resetValue actually changes (user clicked reset), not during normal editing
  useEffect(() => {
    if (previousResetValue.current !== resetValue && !isUserEditing && site.latitude != null && site.longitude != null) {
      const newUtmRef = isUTM_NS
        ? latLngToUTM_NS(site.latitude, site.longitude)
        : latLngToUTMWithBand(site.latitude, site.longitude);
      setValue(utmReferenceField, newUtmRef, { shouldValidate: false });
      previousResetValue.current = resetValue;
    }
  }, [resetValue, isUTM_NS, site.latitude, site.longitude, setValue, utmReferenceField, isUserEditing]);

  const error = get(errors, utmReferenceField);

  return (
    <Form.Group widths="equal">
      <Controller
        control={control}
        name={utmReferenceField}
        defaultValue={utmReference || utmReferenceValue}
        rules={{
          required: true,
          validate: {
            utm: (value) => {
              if (!value || value.trim() === '') {
                return 'UTM coordinate is required';
              }
              const trimmedValue = value.trim();
              const { valid } = parseCoords(trimmedValue);
              if (!valid) {
                return 'Invalid UTM coordinate';
              }
              return true;
            },
          },
        }}
        render={({ field: { ref, onChange, value, ...rest } }) => (
          <Form.Field required>
            <label>UTM Reference:</label>
            <Form.Input
              {...rest}
              ref={ref}
              type="text"
              value={value || ''}
              error={error != null}
              disabled={disabled}
              onChange={(e, { value: newValue }) => {
                setIsUserEditing(true);
                onChange(newValue);
                setModified(true);
                // Only update lat/lng when UTM coordinate is valid and complete
                const trimmedValue = newValue.trim();
                if (trimmedValue.length > 0) {
                  const { valid: newValid, lat: newLat, lng: newLng } = parseCoords(trimmedValue);
                  // UTM coordinates are typically 15-20 characters, so only update if it's reasonably complete
                  if (newValid && newLat != null && newLng != null && trimmedValue.length >= 10) {
                    setValue(latitudeField, newLat, { shouldValidate: false });
                    setValue(longitudeField, newLng, { shouldValidate: false });
                  }
                }
                // Trigger validation after a short delay to ensure value is set
                setTimeout(() => {
                  trigger(utmReferenceField);
                  // Reset editing flag after a delay to allow reset to work
                  setTimeout(() => {
                    setIsUserEditing(false);
                  }, 500);
                }, 0);
              }}
              onBlur={() => {
                // Reset editing flag when user leaves the field
                setTimeout(() => {
                  setIsUserEditing(false);
                }, 100);
              }}
            />
          </Form.Field>
        )}
      />
    </Form.Group>
  );
};
